import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";

export default function PaymentResult() {
  const { result } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(result === "success" ? "checking" : "cancel");
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (result !== "success" || !sessionId) return;
    if (tries > 8) { setState("timeout"); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") setState("paid");
        else if (["failed", "expired"].includes(data.payment_status)) setState("failed");
        else setTries((n) => n + 1);
      } catch { setTries((n) => n + 1); }
    }, 1600);
    return () => clearTimeout(t);
  }, [tries, result, sessionId]);

  const config = {
    checking: { icon: Loader2, spin: true, color: "text-cyan-400", title: "Confirming payment…", text: "Verifying the transaction with Stripe. This takes a moment." },
    paid: { icon: CheckCircle2, color: "text-emerald-400", title: "Payment confirmed", text: "Funds have been booked to your workspace treasury / order marked paid." },
    cancel: { icon: XCircle, color: "text-amber-400", title: "Payment cancelled", text: "No charge was made. You can retry checkout anytime." },
    failed: { icon: XCircle, color: "text-red-400", title: "Payment failed", text: "The payment did not complete. Please try again." },
    timeout: { icon: XCircle, color: "text-amber-400", title: "Still processing", text: "The payment is taking longer than expected. Check the treasury shortly." },
  }[state];
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-[#080B11] flex items-center justify-center p-6">
      <div className="hud-card rounded-xl p-10 max-w-md w-full text-center" data-testid="payment-result">
        <Icon className={`w-14 h-14 mx-auto ${config.color} ${config.spin ? "animate-spin" : ""}`} />
        <h1 className="mt-5 font-display font-extrabold text-2xl">{config.title}</h1>
        <p className="mt-2 text-sm text-slate-400">{config.text}</p>
        <button data-testid="back-to-treasury-btn" onClick={() => navigate("/treasury")} className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-cyan-500 text-[#080B11] font-bold text-sm hover:bg-cyan-400 transition-colors">
          Back to Treasury <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
